// shared-lib/dimensioning/measurementKey.js
//
// ONE identity for a measurement, in the vocabulary the link graph already
// speaks. A measurement is two endpoints and an axis; an endpoint is a typed
// ref (`group-customization/refs.js`). Keying both systems the same way is
// what lets a link be READ as a dimension later — see
// claude-docs/2026-09-15-unified-dimensioning.md §2.
//
// Deliberately does NOT touch `refs.js`. An object ref there keys as
// `object:<id>` with no position, and every stored link relies on that; a
// measurement needs to tell an object's left edge from its centre, so the
// position rides on top HERE and a centre endpoint still keys exactly like a
// bare link ref. Nothing already written changes meaning.

const { normalizeRef, refKey } = require('../group-customization/refs');

// `object-start` / `object-center` / `object-end` — the band the elevation
// generator stamps on a measurement point.
const OBJECT_EDGE = /^object-(start|center|end)$/;

/**
 * The typed ref a measurement endpoint names. PURE.
 *
 * Returns null rather than guessing: an endpoint that cannot be resolved must
 * produce no key at all, so the caller falls back instead of inventing an
 * identity that nothing will ever match again.
 *
 * @param {Object} point  a measurement endpoint (fromPoint / toPoint).
 * @param {{wallId?: string}} ctx  the wall the elevation is drawn from.
 * @returns {Object|null} a `refs.js` ref.
 */
function endpointRef(point, ctx = {}) {
  if (!point || typeof point !== 'object') return null;
  const type = typeof point.type === 'string' ? point.type : '';

  const edge = OBJECT_EDGE.exec(type);
  if (edge) {
    const id = point.object?.id || point.id;
    return id ? { kind: 'object', id, at: edge[1] } : null;
  }

  // Wall ends, and the floor / ceiling lines that bound the drawing. All four
  // are extents of the wall being drawn, so none of them needs a room id.
  const AT_BY_TYPE = {
    'wall-start': 'start',
    'wall-end': 'end',
    floor: 'floor',
    ceiling: 'ceiling',
  };
  const at = AT_BY_TYPE[type];
  if (at) return ctx.wallId ? { kind: 'wall', id: ctx.wallId, at } : null;

  return null;
}

/**
 * Stable string identity for one endpoint. Delegates to `refKey` for every
 * kind it already covers; only an object's position is added here, and only
 * when it is not the centre, so `object:<id>` keeps its existing meaning.
 * PURE.
 */
function endpointKey(ref) {
  const r = normalizeRef(ref);
  if (!r) return null;
  if (r.kind === 'object' && r.at && r.at !== 'center') {
    return `object:${r.id}:${r.at}`;
  }
  return refKey(r);
}

// 'horizontal' / 'vertical' are the elevation generator's own axis names.
const axisToken = (measurement) =>
  measurement?.type === 'vertical' ? 'v' : 'h';

/**
 * The canonical key for a measurement. PURE.
 *
 * Endpoint keys are SORTED: which end a generator happened to emit first is
 * not part of what the dimension measures, and leaving it in the key means a
 * generator reordering silently orphans saved state.
 *
 * @returns {string|null} null when either endpoint cannot be resolved.
 */
function measurementKey(measurement, ctx = {}) {
  if (!measurement) return null;
  const a = endpointKey(endpointRef(measurement.fromPoint, ctx));
  const b = endpointKey(endpointRef(measurement.toPoint, ctx));
  if (!a || !b) return null;
  const [first, second] = [a, b].sort();
  return `dim:${axisToken(measurement)}:${first}|${second}`;
}

/**
 * The key `RenderWallElevation` wrote before ref keys existed, reproduced
 * exactly so offsets saved under it still resolve on read. PURE.
 *
 * Kept as a READ path only — nothing writes this form any more. It embeds the
 * endpoint LABELS, which is why it was replaced: `showNumber` in
 * `generateMeasurementsForSide` renames "Cabinet Left" to "Cabinet 1 Left" as
 * soon as a second cabinet lands on the wall, changing the key of a dimension
 * that did not move and orphaning the user's saved drag.
 */
function legacyElevationOffsetKey(measurement) {
  if (!measurement?.fromPoint || !measurement?.toPoint) return null;

  const pointId = (point) => {
    if (point.object?.id) return point.object.id;
    if (point.id) return point.id;
    if (point.type?.includes('wall')) return 'wall';
    if (point.type?.includes('floor')) return 'floor';
    if (point.type?.includes('ceiling')) return 'ceiling';
    return point.type || 'unknown';
  };

  const fromId = pointId(measurement.fromPoint);
  const toId = pointId(measurement.toPoint);
  const fromLabel = measurement.fromLabel || '';
  const toLabel = measurement.toLabel || '';
  return `${measurement.type}_${fromId}_${fromLabel}_${toId}_${toLabel}`;
}

module.exports = {
  endpointRef,
  endpointKey,
  measurementKey,
  legacyElevationOffsetKey,
};
